import { BoltPromptCommand } from './models/BoltPromptCommand';
import { GroupCommand } from './models/GroupCommand';
import { PromptCommandCategory } from './models/PromptCommandCategory';
import { ViewCommand } from './models/ViewCommand';
import { PromptCreate } from '~/components/chat/commands/views/PromptCreate';
import { sendAnalyticsEvent } from '~/lib/analytics.client';
import { AnalyticsAction, AnalyticsTrackEvent } from '~/types/analytics';

export const PROMPT_CATEGORIES = {
  accessibility: new PromptCommandCategory({
    id: 'accessibility',
    name: 'Accessibility',
    icon: 'i-lucide:accessibility',
  }),

  performance: new PromptCommandCategory({
    id: 'performance',
    name: 'Performance',
    icon: 'i-lucide:zap',
  }),

  seo: new PromptCommandCategory({
    id: 'seo',
    name: 'SEO',
    icon: 'i-lucide:search',
  }),

  security: new PromptCommandCategory({
    id: 'security',
    name: 'Security',
    icon: 'i-lucide:shield',
  }),

  usability: new PromptCommandCategory({
    id: 'usability',
    name: 'Usability',
    icon: 'i-lucide:hand',
  }),

  legal: new PromptCommandCategory({
    id: 'legal',
    name: 'Legal',
    icon: 'i-lucide:scale',
  }),

  misc: new PromptCommandCategory({
    id: 'misc',
    name: 'Misc',
    icon: 'i-lucide:sparkle',
  }),

  workflow: new PromptCommandCategory({
    id: 'workflow',
    name: 'Workflow',
    icon: 'i-lucide:workflow',
  }),
};

// @unocss-include
export const COMMANDS = new GroupCommand({
  id: 'root',
  name: 'Root',
  commands: [
    new ViewCommand({
      id: 'create_prompt',
      name: 'Create Prompt',
      icon: 'i-lucide:message-square-plus',
      view: ({ commandStore, focusPromptInput, command }) => (
        <PromptCreate
          preserveFormValues={command.setFormValues}
          defaultValues={command.getFormValues()}
          categories={commandStore.$promptCategories.get()}
          onCancel={() => {
            commandStore.router.pop();
            command.setFormValues([]);
            focusPromptInput();
          }}
          onCreate={(values) => {
            commandStore.router.pop();

            const category = commandStore.getCategoryById(values.category);

            const prompt = commandStore.addCustomPrompt({
              name: values.name,
              prompt: values.prompt,
              icon: values.icon,
              category: values.category,
            });

            sendAnalyticsEvent({
              action: AnalyticsAction.Track,
              payload: {
                event: AnalyticsTrackEvent.PromptLibraryPromptCreated,
                properties: {
                  promptId: prompt.id,
                  promptTitle: prompt.name,
                  categoryId: category?.id,
                  categoryName: category?.name,
                },
              },
            });
          }}
        />
      ),
    }),

    new BoltPromptCommand({
      id: 'keyboard_navigation_audit',
      name: 'Keyboard Navigation Audit',
      prompt: `Analyze my website's keyboard navigation flow and generate recommendations for improving focus order and keyboard accessibility. Include code snippets for implementing proper tabindex attributes, focus styling, and keyboard event handlers. Ensure all interactive elements are reachable via keyboard alone.`,
      category: PROMPT_CATEGORIES.accessibility,
    }),

    new BoltPromptCommand({
      id: 'focus_management',
      name: 'Focus Management for Single Page Applications',
      prompt: `Generate code to implement proper focus management in my single-page application. Include strategies for returning focus after modal dialogs, maintaining focus when content updates dynamically, and implementing skip links. Ensure compliance with WCAG 2.1 Success Criterion 2.4.3.`,
      category: PROMPT_CATEGORIES.accessibility,
    }),

    new BoltPromptCommand({
      id: 'accessible_data_tables',
      name: 'Accessible Data Tables',
      prompt: `Review my data tables and generate code to enhance their accessibility. Include proper use of caption, thead, tbody, th with scope attributes, and appropriate ARIA attributes. Optimize for screen reader compatibility and keyboard navigation, including sort functionality if applicable.`,
      category: PROMPT_CATEGORIES.accessibility,
    }),

    new BoltPromptCommand({
      id: 'accessible_rich_text_content',
      name: 'Accessible Rich Text Content',
      prompt: `Analyze my text content and provide recommendations for improving readability and accessibility. Include proper heading structure (h1-h6), text spacing, line height, and color contrast. Suggest CSS changes to improve readability for users with dyslexia or visual impairments.`,
      category: PROMPT_CATEGORIES.accessibility,
    }),

    new BoltPromptCommand({
      id: 'aria_landmark_regions',
      name: 'ARIA Landmark Regions',
      prompt: `Audit my page structure and implement proper ARIA landmark regions (banner, navigation, main, complementary, contentinfo). Ensure all content is contained within appropriate landmark roles and screen readers can easily navigate between major sections of the page.`,
      category: PROMPT_CATEGORIES.accessibility,
    }),

    new BoltPromptCommand({
      id: 'internal_linking_strategy',
      name: 'Internal Linking Strategy',
      prompt: `Analyze my website's structure and generate recommendations for an effective internal linking strategy. Identify key pages that need more internal links, suggest anchor text variations, and provide code for implementing breadcrumbs. Include best practices for link attributes and URL structure.`,
      category: PROMPT_CATEGORIES.seo,
    }),

    new BoltPromptCommand({
      id: 'header_hierarchy_optimization',
      name: 'Header Hierarchy Optimization',
      prompt: `Review my HTML header structure (h1-h6) and provide recommendations to optimize for both SEO and accessibility. Ensure proper nesting, keyword usage in headers, and semantic structure that accurately represents the content hierarchy. Include code to fix any issues found.`,
      category: PROMPT_CATEGORIES.seo,
    }),
    new BoltPromptCommand({
      id: 'local_seo_implementation',
      name: 'Local SEO Implementation',
      prompt: `Generate code for implementing local SEO elements on my business website. Include structured data markup for local business, Google Business Profile integration suggestions, location-specific content, and NAP (Name, Address, Phone) consistency implementation across all pages.`,
      category: PROMPT_CATEGORIES.seo,
    }),
    new BoltPromptCommand({
      id: 'content_readability_analysis',
      name: 'Content Readability Analysis',
      prompt: `Analyze my webpage content and provide recommendations to improve readability scores and SEO. Include suggestions for sentence length, paragraph structure, active voice usage, and reading level optimization. Generate sample revisions of problematic text sections.`,
      category: PROMPT_CATEGORIES.seo,
    }),
    new BoltPromptCommand({
      id: 'image_seo_enhancement',
      name: 'Image SEO Enhancement',
      prompt: `Audit my website's images and provide comprehensive recommendations for image SEO. Include guidance on file naming, compression, responsive images with srcset, lazy loading implementation, and image sitemaps. Generate code examples for each optimization technique.`,
      category: PROMPT_CATEGORIES.seo,
    }),

    new BoltPromptCommand({
      id: 'form_simplification',
      name: 'Form Simplification',
      prompt: `Analyze my form design and provide recommendations to simplify the user experience. Generate code for implementing smart defaults, reducing unnecessary fields, logical field grouping, and progressive disclosure techniques. Include validation strategies that minimize user frustration.`,
      category: PROMPT_CATEGORIES.usability,
    }),

    new BoltPromptCommand({
      id: 'navigation_menu_optimization',
      name: 'Navigation Menu Optimization',
      prompt: `Evaluate my website's navigation structure and generate recommendations for improvement based on UX best practices. Consider information architecture, menu depth, naming conventions, and mobile usability. Provide code for implementing a more intuitive navigation system.`,
      category: PROMPT_CATEGORIES.usability,
    }),

    new BoltPromptCommand({
      id: 'input_validation_ux',
      name: 'Input Validation UX',
      prompt: `Generate code for implementing user-friendly form validation that provides clear, immediate feedback. Include strategies for inline validation, error prevention, helpful error messages, and successful submission confirmation. Focus on reducing form abandonment and user frustration.`,
      category: PROMPT_CATEGORIES.usability,
    }),

    new BoltPromptCommand({
      id: 'inclusive_design_patterns',
      name: 'Inclusive Design Patterns',
      prompt: `Review my UI components and suggest inclusive design patterns that work well across diverse users. Generate code for implementing accessible alternatives to common problematic patterns like carousels, infinite scroll, and complex dropdowns. Include progressive enhancement strategies.`,
      category: PROMPT_CATEGORIES.usability,
    }),

    new BoltPromptCommand({
      id: 'microcopy_optimization',
      name: 'Microcopy Optimization',
      prompt: `Analyze the microcopy throughout my interface (button labels, form hints, error messages) and suggest improvements for clarity and user experience. Generate examples of effective microcopy that reduces user confusion and improves task completion rates.`,
      category: PROMPT_CATEGORIES.usability,
    }),

    new BoltPromptCommand({
      id: 'component_documentation_template',
      name: 'Component Documentation Template',
      prompt: `Create a comprehensive template for documenting reusable components in my project. Include sections for component purpose, props/parameters, usage examples, accessibility considerations, and edge cases. Generate sample documentation for a common component like a button or modal.`,
      category: PROMPT_CATEGORIES.workflow,
    }),

    new BoltPromptCommand({
      id: 'api_documentation_generator',
      name: 'API Documentation Generator',
      prompt: `Generate comprehensive documentation for my API endpoints. Include request/response examples, authentication requirements, error handling, and status codes. Create a template that can be expanded as the API grows. Recommend tools for automatic API documentation generation.`,
      category: PROMPT_CATEGORIES.workflow,
    }),

    new BoltPromptCommand({
      id: 'dark_mode_implementation',
      name: 'Dark Mode Implementation',
      prompt: `Generate code for implementing a fully accessible dark mode toggle on my website. Include CSS variables for theming, proper color contrast in both modes, persistent user preference storage, and respect for prefers-color-scheme media query. Ensure smooth transitions between themes.`,
      category: PROMPT_CATEGORIES.misc,
    }),

    new BoltPromptCommand({
      id: 'internationalization_setup',
      name: 'Internationalization Setup',
      prompt: `Create a plan and code samples for implementing internationalization (i18n) in my web application. Include strategies for text translation, date/time formatting, number formatting, and RTL layout support. Recommend libraries and tools for managing translations and locale data.`,
      category: PROMPT_CATEGORIES.misc,
    }),

    new BoltPromptCommand({
      id: 'error_handling_strategy',
      name: 'Error Handling Strategy',
      prompt: `Develop a comprehensive error handling strategy for my web application. Include code for graceful degradation, user-friendly error messages, offline support, and error logging. Provide examples of handling common errors like network failures, API errors, and validation failures.`,
      category: PROMPT_CATEGORIES.misc,
    }),

    new BoltPromptCommand({
      id: 'web_app_manifest_generator',
      name: 'Web App Manifest Generator',
      prompt: `Create a complete web app manifest file to make my website installable as a Progressive Web App. Include appropriate icons, theme colors, display preferences, and orientation settings. Generate the necessary HTML code for linking the manifest and verifying proper implementation.`,
      category: PROMPT_CATEGORIES.misc,
    }),
  ],
});
